import React, { useState } from "react";
import { createPortal } from "react-dom";
import useStudentPortalFetch, {
  StudentPortalShell,
  StudentPortalState,
  formatPortalDate,
  downloadPortalCertificate,
  httpUrl,
  ProgramCover,
  formatPortalMoney,
} from "./useStudentPortalFetch";

function resolveCategory(program) {
  return (
    program?.category?.training_program_category_name ||
    program?.category?.category_name ||
    program?.category_name ||
    "—"
  );
}

function initials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "C";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function batchStatusClass(status) {
  const s = String(status || "").toLowerCase();
  if (s.includes("complete")) return "completed";
  if (s.includes("ongoing") || s.includes("active") || s.includes("pursuing")) {
    return "active";
  }
  if (s.includes("cancel") || s.includes("drop")) {
    return "ems-student-badge-absent";
  }
  return "ems-student-badge-neutral";
}

function moduleMeta(module) {
  return [
    module.duration_in_hours != null && module.duration_in_hours !== ""
      ? `${module.duration_in_hours} hrs`
      : null,
    module.duration_in_days != null && module.duration_in_days !== ""
      ? `${module.duration_in_days} days`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

export default function StudentCoursesPage() {
  const { data, loading, error } = useStudentPortalFetch("courses");
  const admissions = Array.isArray(data) ? data : [];
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadError, setDownloadError] = useState("");
  const [modulesModal, setModulesModal] = useState(null);

  const handleDownload = async (row) => {
    const certificate = row.certificate || null;
    const certificateId = certificate?.organization_ems_certificate_id;
    if (!certificateId || !row.can_download_certificate) return;

    setDownloadError("");
    setDownloadingId(certificateId);
    try {
      await downloadPortalCertificate(
        certificateId,
        certificate.certificate_number ||
          certificate.certificate_name ||
          "certificate",
        certificate.certificate_url
      );
    } catch (err) {
      setDownloadError(
        err?.response?.data?.message ||
          err?.message ||
          "Could not download certificate."
      );
    } finally {
      setDownloadingId(null);
    }
  };

  const modalModules = Array.isArray(modulesModal?.modules)
    ? modulesModal.modules
    : [];

  return (
    <StudentPortalShell
      title="My Courses"
      subtitle="Programs you are enrolled in."
    >
      <StudentPortalState
        loading={loading}
        error={error}
        empty={!loading && !error && admissions.length === 0}
      >
        {downloadError ? (
          <div className="ems-student-portal-state ems-student-portal-error">
            {downloadError}
          </div>
        ) : null}
        <section className="ems-student-section">
          <div className="ems-student-cards ems-course-cards ems-my-courses-list">
            {admissions.map((row) => {
              const program =
                row.training_program || row.trainingProgram || {};
              const name =
                program.training_program_name || "Training program";
              const photoUrl = httpUrl(program.photo_url);
              const notesUrl = httpUrl(program.notes_url);
              const interviewQaUrl = httpUrl(program.interview_qa_url);
              const certificate = row.certificate || null;
              const certificateId =
                certificate?.organization_ems_certificate_id;
              const canDownload = Boolean(
                row.can_download_certificate && certificateId
              );
              const isDownloading = downloadingId === certificateId;
              const modules = Array.isArray(row.modules) ? row.modules : [];
              const batchStatus = row.batch_status || row.batch?.status || "";
              const batchName =
                row.batch?.batch_name || row.batch?.batch_code || "";
              const batchStartDate =
                row.first_class_record_date || null;
              const isFastTrack = /fast\s*track/i.test(
                String(row.duration_track || "")
              );
              const monthsLabel = (() => {
                const n = Number(row.duration_in_months);
                if (!Number.isFinite(n) || n <= 0) return null;
                const rounded =
                  Math.abs(n - Math.round(n)) < 0.001
                    ? String(Math.round(n))
                    : String(n);
                return `${rounded} ${Number(rounded) === 1 ? "Month" : "Months"}`;
              })();
              const hoursLabel = (() => {
                const n = Number(row.duration_in_hours);
                if (!Number.isFinite(n) || n <= 0) return null;
                const rounded =
                  Math.abs(n - Math.round(n)) < 0.001
                    ? String(Math.round(n))
                    : String(n);
                return `${rounded} ${Number(rounded) === 1 ? "Hour" : "Hours"}`;
              })();

              return (
                <article
                  key={row.organization_ems_admission_id}
                  className="ems-student-card ems-course-card ems-my-course-card"
                >
                  <div className="ems-course-card-accent" />
                  <div className="ems-course-card-body">
                    <div className="ems-course-card-top">
                      <ProgramCover
                        src={photoUrl}
                        alt={name}
                        className="ems-course-card-icon"
                      >
                        {initials(name)}
                      </ProgramCover>
                      <div className="ems-course-card-heading">
                        <h3>{name}</h3>
                        <p>{resolveCategory(program)}</p>
                        {batchName ? (
                          <p className="ems-course-batch-code">{batchName}</p>
                        ) : null}
                      </div>
                      {batchStatus ? (
                        <span
                          className={`ems-student-badge ${batchStatusClass(
                            batchStatus
                          )}`}
                        >
                          {batchStatus}
                        </span>
                      ) : null}
                    </div>
                    <div className="ems-course-card-meta">
                      <div>
                        <span>Admission No.</span>
                        <b>{row.admission_number || "—"}</b>
                      </div>
                      <div>
                        <span>Admission Date</span>
                        <b>{formatPortalDate(row.admission_date) || "—"}</b>
                      </div>
                      <div>
                        <span>Net Fee</span>
                        <b>
                          {formatPortalMoney(
                            row.display_net_fee ??
                              row.net_fee_amount_student_currency ??
                              row.net_fee_amount,
                            row.display_currency || row.currency_code || "INR"
                          )}
                        </b>
                      </div>
                      <div>
                        <span>Batch start date</span>
                        <b>{formatPortalDate(batchStartDate) || "—"}</b>
                      </div>
                      {isFastTrack ? (
                        <>
                          <div>
                            <span>Duration</span>
                            <b>
                              {[monthsLabel, hoursLabel]
                                .filter(Boolean)
                                .join(" · ") || "Fast Track"}
                            </b>
                          </div>
                          <div>
                            <span>Estimated Batch End Date</span>
                            <b>
                              {formatPortalDate(
                                row.estimated_batch_end_date
                              ) || "—"}
                            </b>
                          </div>
                        </>
                      ) : null}
                      <div>
                        <span>Modules</span>
                        <b>
                          <button
                            type="button"
                            className="ems-student-link"
                            onClick={() =>
                              setModulesModal({
                                title: name,
                                modules,
                              })
                            }
                          >
                            Open modules ({modules.length})
                          </button>
                        </b>
                      </div>
                      {notesUrl ? (
                        <div>
                          <span>Notes</span>
                          <b>
                            <a
                              className="ems-student-link"
                              href={notesUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Open notes
                            </a>
                          </b>
                        </div>
                      ) : null}
                      {interviewQaUrl ? (
                        <div>
                          <span>Interview Q/A</span>
                          <b>
                            <a
                              className="ems-student-link"
                              href={interviewQaUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Open Q/A
                            </a>
                          </b>
                        </div>
                      ) : null}
                      <div>
                        <span>Certificate</span>
                        <b>
                          {canDownload ? (
                            <button
                              type="button"
                              className="ems-student-link"
                              disabled={isDownloading}
                              onClick={() => handleDownload(row)}
                            >
                              {isDownloading ? "Downloading…" : "Download"}
                            </button>
                          ) : (
                            "Not available"
                          )}
                        </b>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      </StudentPortalState>

      {modulesModal
        ? createPortal(
            <div
              className="ems-student-modal-backdrop"
              role="presentation"
              onClick={() => setModulesModal(null)}
            >
              <div
                className="ems-student-modal ems-course-modules-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="ems-course-modules-title"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="ems-student-modal-header">
                  <h3 id="ems-course-modules-title">
                    {modulesModal.title || "Open modules"}
                  </h3>
                  <button
                    type="button"
                    className="ems-student-modal-close"
                    onClick={() => setModulesModal(null)}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>
                <div className="ems-student-modal-body">
                  <div className="ems-course-modules is-open">
                    <div className="ems-course-modules-head">
                      <strong>Open modules</strong>
                      <span>{modalModules.length}</span>
                    </div>
                    {modalModules.length === 0 ? (
                      <p className="ems-student-meta">
                        No modules configured for this program.
                      </p>
                    ) : (
                      <ul className="ems-course-modules-list">
                        {modalModules.map((module) => (
                          <li
                            key={
                              module.organization_ems_training_program_module_id ||
                              module.module_name
                            }
                          >
                            <strong>{module.module_name || "Module"}</strong>
                            {moduleMeta(module) ? (
                              <span>{moduleMeta(module)}</span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className="ems-student-modal-footer">
                  <button
                    type="button"
                    className="ems-student-btn ems-student-btn-secondary ems-student-btn-sm"
                    onClick={() => setModulesModal(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </StudentPortalShell>
  );
}
